/**
 * The Purview search builder (R2-12) for a case that already exists, from the
 * case menu: Purview exports take hours, so the new-case wizard is long closed
 * by the time one is ready. When Purview is connected, Run in Purview (sc-11767)
 * runs the search there and imports the export into this case; otherwise the
 * dialog says how to add the files by hand.
 */
import { useEffect, useState } from 'react';
import { Filter, X } from 'lucide-react';
import { intakeApi } from '../../lib/api';
import type { Case, EDiscoverySource } from '../../types';
import { cn } from '../ui/utils';
import { PurviewQueryBuilder } from './PurviewQueryBuilder';
import { purviewRequestMeta } from './purviewSearch';

interface PurviewQueryDialogProps {
  open: boolean;
  onClose: () => void;
  caseData: Case;
  /** Run in Purview started: the case page follows the import. */
  onRunStarted?: (source: EDiscoverySource) => void;
}

export function PurviewQueryDialog({ open, onClose, caseData, onRunStarted }: PurviewQueryDialogProps) {
  // Kept (hidden) after the first open, so closing it doesn't lose the query.
  const [opened, setOpened] = useState(open);
  useEffect(() => {
    if (open) setOpened(true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Run in Purview is offered only when the tenant has Purview connected.
  const [canRun, setCanRun] = useState(false);
  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    intakeApi.listEDiscoverySystems()
      .then((systems) => { if (!cancelled) setCanRun(systems.includes('purview')); })
      .catch(() => { /* copy only */ });
    return () => { cancelled = true; };
  }, [opened]);

  if (!open && !opened) return null;
  return (
    <div className={cn('fixed inset-0 z-[100] flex items-start justify-center bg-black/50 pt-[6vh]', !open && 'hidden')}
      role="dialog"
      aria-modal="true" aria-label="Purview search query">
      <div className="flex max-h-[88vh] w-[1000px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="flex min-w-0 items-center gap-2">
            <Filter className="h-5 w-5 shrink-0 text-primary" />
            <div className="min-w-0">
              <p className="text-xs text-gray-500">{caseData.name}</p>
              <h2 className="font-title text-lg font-semibold">Purview search query</h2>
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-gray-100" aria-label="Close">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <PurviewQueryBuilder caseId={caseData.id} context="case" requestText={caseData.notes ?? ''}
            requestMeta={purviewRequestMeta(caseData)} canRunInPurview={canRun} onRunStarted={onRunStarted} />
        </div>
      </div>
    </div>
  );
}
